'use client'

import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'

export interface ModalProps {
  open: boolean
  onClose: () => void
  width?: number
  children: React.ReactNode
  className?: string
}

export function Modal({ open, onClose, width = 440, children, className }: ModalProps) {
  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    if (open) document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, handleEsc])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'var(--surface-overlay)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className={cn('rounded-xl overflow-hidden', className)}
        style={{
          width,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100vh - 48px)',
          background: 'var(--surface-primary)',
          boxShadow: 'var(--shadow-xl)',
          animation: 'modal-in 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

export interface ModalHeaderProps {
  onClose?: () => void
  children: React.ReactNode
}

export function ModalHeader({ onClose, children }: ModalHeaderProps) {
  return (
    <div
      className="flex items-center justify-between px-5 h-12"
      style={{ borderBottom: '1px solid var(--border-muted)' }}
    >
      <span className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>
        {children}
      </span>
      {onClose && (
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-md transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          <X size={15} strokeWidth={1.75} />
        </button>
      )}
    </div>
  )
}

export function ModalBody({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>
}

export function ModalFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('flex items-center justify-end gap-2 px-5 py-3', className)}
      style={{ borderTop: '1px solid var(--border-muted)' }}
    >
      {children}
    </div>
  )
}
